import { useSelector } from '../redux/store';
import useFeaturePermission from './useFeaturePermission';

// Reads the two forms feature flags and returns { v1Enabled, v2Enabled, activeFormsVersion }.
//
// Product rule: V2 supersedes V1. When V2 is enabled, V1 is treated as off
// regardless of what the backend says about forms-v1 — this keeps every
// consumer consistent without each one having to re-implement the priority.
//
// TODO: remove the `consent_version` fallback below once the backend
// migration is fully deployed in prod (rollout date 2026-04-24).
export default function useFormsFlags() {
    const rawV1 = useFeaturePermission('forms-v1').isEnabled;
    const rawV2 = useFeaturePermission('forms-v2').isEnabled;

    const choicesData = useSelector((state) => state.practiceTreatment?.choicesData?.results);

    let v1Enabled = rawV1;
    let v2Enabled = rawV2;

    // Legacy fallback during the rollout window. Only fires when neither
    // backend flag is set, so a successful permissions response always wins.
    if (!rawV1 && !rawV2 && Array.isArray(choicesData)) {
        const config = choicesData.find((c) => {
            const m = typeof c?.metadata === 'string'
                ? (() => { try { return JSON.parse(c.metadata || '{}'); } catch { return {}; } })()
                : (c?.metadata || {});
            return m.type === 'consent_form_config';
        });
        if (config) {
            const m = typeof config.metadata === 'string'
                ? (() => { try { return JSON.parse(config.metadata); } catch { return {}; } })()
                : (config.metadata || {});
            const legacy = m.consent_version;
            if (legacy === 'v2') {
                v2Enabled = true;
            } else if (legacy === 'v1') {
                v1Enabled = true;
            }
        }
    }

    // V2 supersedes V1. After this normalisation v1Enabled and v2Enabled
    // are mutually exclusive, so consumers can render against either flag
    // without re-implementing precedence.
    if (v2Enabled) {
        v1Enabled = false;
    }

    let activeFormsVersion = null;
    if (v2Enabled) activeFormsVersion = 'v2';
    else if (v1Enabled) activeFormsVersion = 'v1';

    return { v1Enabled, v2Enabled, activeFormsVersion };
}
